/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Link,
  LinearProgress,
  Typography,
} from '@material-ui/core';
import {
  CheckCircleOutline as CheckIcon,
  ErrorOutline as ErrorIcon,
  Home as HomeIcon,
  Launch as LaunchIcon,
} from '@material-ui/icons';
import { BridgeClient } from '../../../../app/service/bridge/BridgeClient';
import { enqueueErrorSnackbar } from '../../../../actions/Notifications';
import { useIndexingProgress } from '../../hooks/useIndexingProgress';
import WizardStepLayout from '../../components/common/WizardStepLayout';
import { useOperatorState } from '../../../home/hooks/useOperatorState';
import { deriveIndexingView } from '../../../integration/shared/deriveIndexingView';
import { pluginAppInIframe } from '../../../../util/appUtils';
import TawkHelper from '../../../../util/tawkHelper';
import { markWizardCompleted } from '../../../shell/session/shellSessionStorage';
import { useShellAppContext } from '../../../shell/context/useShellAppContext';
import {
  supportsMarketplacePluginBridgeShape,
  supportsPluginArtifactMutationSnapshot,
} from '../../../shell/pluginCapabilities';
import { usePreviewOpener } from '../../../shell/preview/usePreviewOpener';
import { shellLogger } from '../../../shell/logging/shellLogger';
import { shellIncidentReporter } from '../../../shell/logging/shellIncidentReporter';
import { publishJobArtifactsToBridge } from '../../../search-settings/gateway/publishJobArtifactsToBridge';
import { telemetryClient } from '../../../telemetry/telemetryClient';
import { deriveWizardSummaryState } from './deriveWizardSummaryState';
import { ADMIN_RUNTIME_COMMANDS } from '../../../../app/runtime/adminRuntime';
import styles from './SummaryStep.module.scss';
import IntlMessages from 'util/IntlMessages';
import { useLocalizedMessage } from 'util/useLocalizedMessage';

const STATUS_POLL_INTERVAL_MS = 5000;
const BUILD_POLL_INTERVAL_MS = 5000;
const BUILD_POLL_TIMEOUT_MS = 360000;

function isBuildSuccessful(status) {
  return status === 'success';
}

function getErrorMessage(error, fallback = null) {
  if (!error) {
    return null;
  }

  if (typeof error === 'string') {
    return error;
  }

  return error.message || error.error || fallback;
}
// through the shared deriveIndexingView helper. The wizard MUST use
// readinessCanSearch (not raw status alone) to decide when to say
function getIndexingCopy({ rawStatus, readinessCanSearch, progress, jobId, error, t }) {
  const view = deriveIndexingView({
    rawStatus,
    readinessCanSearch,
    jobId,
    error,
    progress,
  });
  const progressPct = typeof progress === 'number' ? progress : 0;

  if (view.viewStatus === 'ready') {
    return {
      title: t('ui.integrationWizard.step5Summary.yourCatalogIsReady'),
      description: t('ui.integrationWizard.step5Summary.searchIsReadyToUseYouCanOpenThePreview'),
      detail: t('ui.integrationWizard.step5Summary.indexIsLiveAndTheStorefrontWillReturnMatchingProducts'),
    };
  }
  if (view.viewStatus === 'failed') {
    return {
      title: t('ui.integrationWizard.step5Summary.indexingCouldNotFinish'),
      description: t('ui.integrationWizard.step5Summary.thePluginReportedAFailureWhileBuildingTheIndexYou'),
      detail: error ? String(error) : t('ui.integrationWizard.step5Summary.indexingFailed'),
    };
  }
  if (view.viewStatus === 'in_progress') {
    if (!readinessCanSearch && progressPct >= 100) {
      return {
        title: t('ui.integrationWizard.step5Summary.finalizingYourIndex'),
        description: t('ui.integrationWizard.step5Summary.catalogProcessingReached100TheFinalSearchDataIsStill'),
        detail: t('ui.integrationWizard.step5Summary.finalizationIsRunningPreviewWillOpenAutomaticallyWhenSearchIs'),
      };
    }
    return {
      title: t('ui.integrationWizard.step5Summary.processingYourCatalog'),
      description: t('ui.integrationWizard.step5Summary.yourPluginIsStillIndexingProductsBeforePreviewIsReady'),
      detail: t('ui.integrationWizard.step5Summary.catalogProcessingProgress', { progress: progressPct }),
    };
  }
  if (view.viewStatus === 'draining') {
    // queued OR completed-without-can_search (the brief final-install window).

    const isFinalizing = rawStatus === 'completed' || rawStatus === 'done';
    if (isFinalizing) {
      return {
        title: t('ui.integrationWizard.step5Summary.finalizingYourIndex'),
        description: t('ui.integrationWizard.step5Summary.theIndexJobFinishedWeAreVerifyingItIs'),
        detail: t('ui.integrationWizard.step5Summary.thisUsuallyTakesAFewSeconds'),
      };
    }
    return {
      title: t('ui.integrationWizard.step5Summary.startingYourIndexer'),
      description: t('ui.integrationWizard.step5Summary.aWorkerWillPickUpTheIndexingJobShortly'),
      detail: jobId
        ? t('ui.integrationWizard.step5Summary.jobQueued', { jobId })
        : t('ui.integrationWizard.step5Summary.waitingForWorker'),
    };
  }
  // view.viewStatus === 'idle' (rawStatus === 'idle' OR null/undefined)
  if (rawStatus == null) {
    return {
      title: t('ui.integrationWizard.step5Summary.checkingYourStoreSetup'),
      description: t('ui.integrationWizard.step5Summary.weAreReadingPluginOwnedIndexingAndReadinessStateForYour'),
      detail: t('ui.integrationWizard.step5Summary.statusWillRefreshAutomatically'),
    };
  }
  return {
    title: t('ui.integrationWizard.step5Summary.preparingYourSearchPreview'),
    description: t('ui.integrationWizard.step5Summary.weAreWaitingForThePluginToFinishPreparingSearch'),
    detail: t('ui.integrationWizard.step5Summary.catalogProcessingHasNotReportedProgressYet'),
  };
}

function getHomeHandoffPath(shopId) {
  // In iframe mode the wizard can run long enough for the bootstrap-time shell
  // session snapshot to expire. Re-enter via /embedded/app so the shell
  // bootstrap can fetch and persist a fresh session before normal /app render.
  return pluginAppInIframe() ? '/embedded/app' : `/app/${shopId}/home`;
}

/**
 * Read onboarding state from the plugin bridge.
 * Returns { state, degraded } — `degraded` is true when the plugin is too old
 * to support `onboarding.state` and returned an error-typed payload instead of
 * throwing.  The admin must degrade coherently in that case.
 */
async function readOnboardingState(bridge) {
  const response = await bridge.sendMessageWithResponse('onboarding.state', {});
  // payload like { result: 'error', error: 'Unknown action: onboarding.state' }.
  // Treat this as "plugin too old" — degrade gracefully instead of spinning.
  if (response.result === 'error') {
    return { state: null, degraded: true, pluginError: response.error || null };
  }

  return { state: response.state || null, degraded: false, pluginError: null };
}

const SummaryStep = ({ shopId, onReady, client }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const t = useLocalizedMessage();
  const { operatorState } = useOperatorState();
  const {
    indexingStatus,
    indexingProgress,
    activeIndexPresent,
    error: indexingErrorFromHook,
    rawIndexingStatus,
    jobId,
    readinessCanSearch,
    retrigger,
  } = useIndexingProgress();
  const bridgeRef = useRef(null);
  const completionRequestedRef = useRef(false);
  const buildRequestedRef = useRef(false);
  const previewCheckpointRequestedRef = useRef(false);
  const jobIdPersistedRef = useRef(false);
  // Unmount-safety: prevents setState calls after the component has been
  // destroyed (e.g. navigation away while an async bridge call is in-flight).
  const mountedRef = useRef(true);
  // Observability: flowId correlation (read once from session snapshot on mount).
  const flowIdRef = useRef(null);
  // Wall-clock start of the in-flight onboarding.complete call for durationMs.
  const completionStartMsRef = useRef(null);
  const [onboardingState, setOnboardingState] = useState(null);
  const [bridgeError, setBridgeError] = useState(null);
  const [isDegraded, setIsDegraded] = useState(false);
  const [buildJobId, setBuildJobId] = useState(null);
  const [buildStatus, setBuildStatus] = useState('idle');
  const [buildError, setBuildError] = useState(null);
  const onboardingShopUrl = onboardingState?.context?.shop_url
    || onboardingState?.context?.shopUrl
    || null;
  const { previewShopAddress, openStorefrontPreview } = usePreviewOpener({
    pluginShopUrl: onboardingShopUrl,
  });
  // Guards the Go-to-Home CTA during the onboarding.complete in-flight window
  // so the user can’t trigger extra interactions while the redirect is pending.
  const [isCompleting, setIsCompleting] = useState(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);
  // for the wizard lifecycle).  Falls back to null when the shell session is
  // absent, expired, or pre-10Q.
  const {
    flowId: shellFlowId,
    gatewayClient,
    capabilities,
    runtime,
  } = useShellAppContext();
  const liveArtifactMutationCapability = supportsPluginArtifactMutationSnapshot(onboardingState)
    || (
      capabilities?.artifactMutationV1 === true
      && supportsMarketplacePluginBridgeShape(onboardingState)
    );
  const artifactMutationSupported = (
    capabilities?.artifactMutationV1 === true || capabilities?.sharedAdminRuntimeV1 === true
  )
    && liveArtifactMutationCapability
    && runtime?.supports?.(ADMIN_RUNTIME_COMMANDS.BUNDLE_BUILD_PREVIEW) === true;
  useEffect(() => {
    flowIdRef.current = shellFlowId || null;
  }, [shellFlowId]);

  useEffect(() => {
    if (onReady) {
      onReady(true);
    }
  }, [onReady]);

  useEffect(() => {
    if (!bridgeRef.current) {
      bridgeRef.current = new BridgeClient();
    }

    let cancelled = false;

    const pollState = async () => {
      try {
        const { state: nextState, degraded } = await readOnboardingState(bridgeRef.current);
        if (!cancelled) {
          if (degraded) {
            setIsDegraded(true);
            // Stop polling — older plugin won't suddenly start supporting the action.
            window.clearInterval(intervalId);
          } else {
            setOnboardingState(nextState);
          }
          setBridgeError(null);
        }
      } catch (error) {
        if (!cancelled) {
          setBridgeError(error);
        }
      }
    };

    pollState();
    const intervalId = window.setInterval(pollState, STATUS_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, []);
  // Hook data (indexingStatus, indexingProgress, etc.) is only used as fallback
  // for degraded mode (older plugin without onboarding.state support).
  const {
    wizardCompleted,
    previewBuilt,
    priorCheckpointsMet,
    storedBuildJobId,
    storeReady,
    hasError,
    isReady,
    isCheckingState,
    isBuildingPreview,
    indexingProgressForUI,
    indexingError,
  } = deriveWizardSummaryState({
    onboardingState,
    isDegraded,
    operatorStateFallback: operatorState,
    indexingFromHook: {
      indexingStatus,
      indexingProgress,
      activeIndexPresent,
      error: indexingErrorFromHook,
      rawIndexingStatus,
      jobId,
      readinessCanSearch,
    },
    buildStatus,
    buildError,
    bridgeError,
  });
  // source for the copy. Hook data is the fallback for older plugins.
  // Both paths feed the shared deriveIndexingView helper so the wizard
  // says "Ready" only when readinessCanSearch=true, not when raw status

  const indexingCopy = getIndexingCopy({
    rawStatus: isDegraded ? rawIndexingStatus : (onboardingState?.indexing?.status ?? rawIndexingStatus),
    readinessCanSearch: isDegraded ? readinessCanSearch : (onboardingState?.readiness?.can_search === true || readinessCanSearch),
    progress: isDegraded ? indexingProgress : (onboardingState?.indexing?.progress ?? indexingProgress),
    jobId: isDegraded ? jobId : (onboardingState?.indexing?.job_id ?? jobId),
    error: indexingError,
    t,
  });
  const errorMessage = getErrorMessage(buildError)
    || getErrorMessage(bridgeError)
    || getErrorMessage(indexingError)
    || t('ui.integrationWizard.step5Summary.statusReadFailed');
  // the plugin state already contains a job id (written by the checkpoint below)
  // and the build is not yet complete, seed `buildJobId` so the polling effect
  // picks it up without re-starting the gateway preview build.
  useEffect(() => {
    if (!storedBuildJobId || previewBuilt || buildJobId) {
      return;
    }
    // Mark as already requested so the trigger effect below does not fire a
    // duplicate preview build while we are resuming polling.
    buildRequestedRef.current = true;
    setBuildJobId(storedBuildJobId);
    setBuildStatus('queued');
  }, [storedBuildJobId, previewBuilt, buildJobId]);

  useEffect(() => {
    if (!artifactMutationSupported || isDegraded || !onboardingState || !storeReady || !priorCheckpointsMet
        || previewBuilt || buildRequestedRef.current) {
      return;
    }

    buildRequestedRef.current = true;
    setBuildError(null);
    setBuildStatus('queued');
    telemetryClient.capture('wizard_preview_build_started', {
      step_id: 'summary',
      step_index: 4,
    });

    if (!gatewayClient || typeof gatewayClient.buildPreviewBundle !== 'function') {
      buildRequestedRef.current = false;
      setBuildStatus('failed');
      telemetryClient.capture('wizard_preview_build_failed', {
        step_id: 'summary',
        step_index: 4,
        normalized_reason_code: 'gateway_unavailable',
      });
      const error = new Error('Gateway session is not available. Reopen the embedded admin and try again.');
      setBuildError(error);
      dispatch(enqueueErrorSnackbar(getErrorMessage(error, t('ui.integrationWizard.step5Summary.previewBuildStartFailed'))));
      return;
    }

    Promise.resolve()
      .then(async () => {
        if (client && typeof client.persistDraftToGateway === 'function') {
          await client.persistDraftToGateway(shopId, { production: true });
        }

        return gatewayClient.buildPreviewBundle(shopId);
      })
      .then((response) => {
        if (!mountedRef.current) return;

        const nextJobId = response?.job_id || response?.jobId || response?.job?._id || response?.job?.jobId;
        if (!nextJobId) {
          throw new Error('Preview build did not return a job id');
        }

        setBuildJobId(nextJobId);
        setBuildStatus(
          response?.jobStatus
          || response?.job?.status
          || (response?.status === 'accepted' ? 'queued' : response?.status)
          || 'queued',
        );
        // this job instead of triggering a duplicate build.
        if (!jobIdPersistedRef.current) {
          jobIdPersistedRef.current = true;
          bridgeRef.current.sendMessageWithResponse('onboarding.checkpoint', {
            checkpoint: 'preview_build_job_id',
            value: nextJobId,
          }).catch(() => {
            // Non-fatal: the job id persist failing just means a reload would
            // start a new build, which is acceptable as a fallback.
            jobIdPersistedRef.current = false;
          });
        }
      })
      .catch((error) => {
        if (!mountedRef.current) return;
        buildRequestedRef.current = false;
        setBuildStatus('failed');
        telemetryClient.capture('wizard_preview_build_failed', {
          step_id: 'summary',
          step_index: 4,
          normalized_reason_code: 'preview_build_start_failed',
        });
        setBuildError(error);
        dispatch(enqueueErrorSnackbar(getErrorMessage(error, t('ui.integrationWizard.step5Summary.previewBuildStartFailed'))));
      });
  }, [artifactMutationSupported, client, dispatch, gatewayClient, isDegraded, onboardingState, previewBuilt, priorCheckpointsMet, shopId, storeReady]);

  useEffect(() => {
    if (!artifactMutationSupported || isDegraded || !buildJobId || previewBuilt) {
      return undefined;
    }

    if (!gatewayClient || typeof gatewayClient.pollJobStatus !== 'function') {
      setBuildStatus('failed');
      setBuildError(new Error('Gateway session is not available. Reopen the embedded admin and try again.'));
      return undefined;
    }

    let cancelled = false;

    const pollBuildJob = async () => {
      try {
        const response = await gatewayClient.pollJobStatus(buildJobId, {
          intervalMs: BUILD_POLL_INTERVAL_MS,
          timeoutMs: BUILD_POLL_TIMEOUT_MS,
        });

        const nextStatus = response?.status || response?.job?.status || 'success';
        if (cancelled) {
          return;
        }

        setBuildStatus(nextStatus);
        setBuildError(null);

        if (nextStatus === 'failed') {
          telemetryClient.capture('wizard_preview_build_failed', {
            step_id: 'summary',
            step_index: 4,
            normalized_reason_code: 'preview_build_failed',
          });
          const nextError = response?.job?.error || 'Preview build failed';
          setBuildError(nextError);
          setBuildJobId(null);
          // Keep buildRequestedRef=true after a terminal failure. Otherwise
          // preview_built=false still satisfies the trigger effect and starts
          // a new preview build again on every failed poll.
          setOnboardingState((currentState) => ({
            ...(currentState || {}),
            onboarding: {
              ...(currentState?.onboarding || {}),
              preview_build_job_id: null,
            },
          }));
          return;
        }

        if (isBuildSuccessful(nextStatus) && !previewCheckpointRequestedRef.current) {
          previewCheckpointRequestedRef.current = true;

          if (!bridgeRef.current) {
            bridgeRef.current = new BridgeClient();
          }

          await publishJobArtifactsToBridge({
            gatewayClient,
            bridgeClient: bridgeRef.current,
            jobId: buildJobId,
          });

          const checkpointResponse = await bridgeRef.current.sendMessageWithResponse('onboarding.checkpoint', {
            checkpoint: 'preview_built',
            value: true,
          });

          if (checkpointResponse && checkpointResponse.result === 'error') {
            throw new Error(getErrorMessage(checkpointResponse, t('ui.integrationWizard.step5Summary.checkpointSaveFailed')));
          }

          if (!cancelled) {
            telemetryClient.capture('wizard_preview_build_succeeded', {
              step_id: 'summary',
              step_index: 4,
            });
            setOnboardingState((currentState) => ({
              ...(currentState || {}),
              onboarding: {
                ...(currentState?.onboarding || {}),
                preview_built: true,
              },
            }));
          }
        }
      } catch (error) {
        if (!cancelled) {
          telemetryClient.capture('wizard_preview_build_failed', {
            step_id: 'summary',
            step_index: 4,
            normalized_reason_code: 'preview_publish_failed',
          });
          previewCheckpointRequestedRef.current = false;
          setBuildJobId(null);
          setBuildStatus('failed');
          setBuildError(error);
          setOnboardingState((currentState) => ({
            ...(currentState || {}),
            onboarding: {
              ...(currentState?.onboarding || {}),
              preview_build_job_id: null,
            },
          }));
        }
      }
    };

    pollBuildJob();

    return () => {
      cancelled = true;
    };
  }, [artifactMutationSupported, buildJobId, gatewayClient, isDegraded, previewBuilt]);

  useEffect(() => {
    // In degraded mode the plugin doesn't support onboarding.complete either.
    if (isDegraded) {
      return;
    }

    if (!onboardingState || !isReady || !priorCheckpointsMet || !previewBuilt
        || wizardCompleted || completionRequestedRef.current) {
      return;
    }

    if (!bridgeRef.current) {
      bridgeRef.current = new BridgeClient();
    }

    completionRequestedRef.current = true;
    completionStartMsRef.current = Date.now();
    shellLogger.info('wizard.complete.start', { flowId: flowIdRef.current, shopId });

    setIsCompleting(true);
    bridgeRef.current.sendMessageWithResponse('onboarding.complete', {
      shopId,
      wizardType: 'integration-wizard-v2',
    })
      .then((response) => {
        // Plugin may reject with { result: 'error' } (e.g. PREREQUISITE_CHECKPOINTS_MISSING).
        // BridgeClient resolves any ACTION_SUCCEEDED payload, so check result explicitly.
        if (response && response.result === 'error') {
          completionRequestedRef.current = false;
          telemetryClient.capture('wizard_step_validation_blocked', {
            step_id: 'summary',
            step_index: 4,
            normalized_reason_code: 'wizard_completion_rejected',
          });
          shellLogger.warn('wizard.complete.error', {
            flowId: flowIdRef.current,
            shopId,
            errorCode: response.error || 'PLUGIN_ERROR',
            durationMs: completionStartMsRef.current
              ? Date.now() - completionStartMsRef.current : null,
          });
          shellIncidentReporter.report('wizard.complete.error', {
            flowId: flowIdRef.current,
            shopId,
            route: null,
            stage: 'wizard',
            errorCode: response.error || 'PLUGIN_ERROR',
            status: 'error',
            durationMs: completionStartMsRef.current
              ? Date.now() - completionStartMsRef.current : null,
          });
          if (mountedRef.current) {
            setIsCompleting(false);
            dispatch(enqueueErrorSnackbar(
              getErrorMessage(response, t('ui.integrationWizard.step5Summary.pluginRejectedCompletion')),
            ));
          }
          return;
        }

        shellLogger.info('wizard.complete.success', {
          flowId: flowIdRef.current,
          shopId,
          durationMs: completionStartMsRef.current
            ? Date.now() - completionStartMsRef.current : null,
        });
        telemetryClient.capture('wizard_completed', {
          step_id: 'summary',
          step_index: 4,
          result_state: 'completed',
        });
        // Persist wizard completion before navigating so the next bootstrap /
        // shell gate cycle sees the completed flag immediately.
        markWizardCompleted();
        // Update local state only if still mounted (redirect may have already
        // unmounted the component in a fast-render environment).
        if (mountedRef.current) {
          setOnboardingState((currentState) => ({
            ...(currentState || {}),
            onboarding: {
              ...(currentState?.onboarding || {}),
              wizard_completed: true,
            },
          }));
        }
        // In iframe mode do not jump straight into /app/:shopId/home with the
        // potentially stale bootstrap-time session snapshot. Route through the
        // canonical bootstrap entry so the shell can refresh session state
        // before landing in the normal app shell.
        history.replace(getHomeHandoffPath(shopId));
      })
      .catch((error) => {
        telemetryClient.capture('wizard_step_validation_blocked', {
          step_id: 'summary',
          step_index: 4,
          normalized_reason_code: 'wizard_completion_failed',
        });
        completionRequestedRef.current = false;
        shellLogger.warn('wizard.complete.error', {
          flowId: flowIdRef.current,
          shopId,
          errorCode: error?.errorCode || error?.code || null,
          durationMs: completionStartMsRef.current
            ? Date.now() - completionStartMsRef.current : null,
        });
        shellIncidentReporter.report('wizard.complete.error', {
          flowId: flowIdRef.current,
          shopId,
          route: null,
          stage: 'wizard',
          errorCode: error?.errorCode || error?.code || 'UNKNOWN',
          status: 'error',
          durationMs: completionStartMsRef.current
            ? Date.now() - completionStartMsRef.current : null,
        });
        if (mountedRef.current) {
          setIsCompleting(false);
          dispatch(enqueueErrorSnackbar(getErrorMessage(error, t('ui.integrationWizard.step5Summary.progressSaveFailed'))));
        }
      });
  }, [dispatch, isDegraded, isReady, onboardingState, previewBuilt, priorCheckpointsMet, shopId, wizardCompleted]);

  const handleRetry = async () => {
    buildRequestedRef.current = false;
    previewCheckpointRequestedRef.current = false;
    jobIdPersistedRef.current = false;
    setBuildJobId(null);
    setBuildStatus('idle');
    setBuildError(null);
    setOnboardingState((currentState) => ({
      ...(currentState || {}),
      onboarding: {
        ...(currentState?.onboarding || {}),
        preview_build_job_id: null,
        preview_built: false,
      },
    }));
    retrigger();
    try {
      if (!bridgeRef.current) {
        bridgeRef.current = new BridgeClient();
      }

      await bridgeRef.current.sendMessageWithResponse('onboarding.checkpoint', {
        checkpoint: 'preview_build_job_id',
        value: null,
      });

      const { state: nextState, degraded } = await readOnboardingState(bridgeRef.current);
      if (!mountedRef.current) return;
      if (degraded) {
        setIsDegraded(true);
      } else {
        setOnboardingState(nextState);
      }
      setBridgeError(null);
    } catch (error) {
      if (!mountedRef.current) return;
      setBridgeError(error);
      dispatch(enqueueErrorSnackbar(getErrorMessage(error, t('ui.integrationWizard.step5Summary.statusRefreshFailed'))));
    }
  };

  const handleOpenPreview = async () => {
    if (!previewShopAddress) {
      dispatch(enqueueErrorSnackbar(t('ui.integrationWizard.step5Summary.storeAddressMissing')));
      return;
    }

    try {
      await openStorefrontPreview();

      telemetryClient.capture('wizard_preview_opened', {
        step_id: 'summary',
        step_index: 4,
      });

    } catch (err) {
      dispatch(enqueueErrorSnackbar(err.message || t('ui.integrationWizard.step5Summary.previewOpenFailed')));
    }
  };

  const handleGoHome = () => {
    history.push(getHomeHandoffPath(shopId));
  };

  const handleContactSupport = () => {
    TawkHelper.openSupportChat();
  };

  return (
    <WizardStepLayout>
      <Box className={styles.statusCard}>
        {isCheckingState && !hasError && !isDegraded && (
          <>
            <CircularProgress size={60} thickness={4} style={{ marginBottom: 24, color: 'var(--kea-color-primary)' }} />
            <Typography variant="h5" className={styles.title}>
              <IntlMessages id="ui.integrationWizard.step5Summary.checkingYourStoreSetup" />
            </Typography>
            <Typography variant="body1" className={styles.description}>
              <IntlMessages id="ui.integrationWizard.step5Summary.weAreReadingPluginOwnedIndexingAndReadinessStateForYour" />
            </Typography>
          </>
        )}

        {!isCheckingState && !hasError && !isReady && !storeReady && !isDegraded && (
          <>
            <CircularProgress size={60} thickness={4} style={{ marginBottom: 24, color: 'var(--kea-color-primary)' }} />
            <Typography variant="h5" className={styles.title}>
              {indexingCopy.title}
            </Typography>
            <Typography variant="body1" className={styles.description}>
              {indexingCopy.description}
            </Typography>

            <Box width="100%" maxWidth={360} mt={1}>
              <Typography variant="body2" color="textSecondary" data-testid="wizard-indexing-status">
                {indexingCopy.detail}
              </Typography>
              {typeof indexingProgressForUI === 'number' && indexingProgressForUI > 0 && (
                <Box mt={2}>
                  <LinearProgress variant="determinate" value={indexingProgressForUI} />
                </Box>
              )}
            </Box>

            <Box width="100%" maxWidth={360} mt={2}>
              <Typography variant="body2" color="textSecondary" data-testid="wizard-preview-build-status">
                <IntlMessages id="ui.integrationWizard.step5Summary.previewBundleWillStartOnceCatalogIsReady" />
              </Typography>
            </Box>
          </>
        )}

        {!isCheckingState && !hasError && !isDegraded && storeReady && !previewBuilt
          && !artifactMutationSupported && (
          <>
            <ErrorIcon className={styles.icon} color="error" />
            <Typography variant="h5" color="error" className={styles.title}>
              <IntlMessages id="ui.integrationWizard.step5Summary.updateTheSearchPlugin" />
            </Typography>
            <Typography variant="body1" className={styles.description}>
              <IntlMessages id="ui.integrationWizard.step5Summary.safePreviewPublishingRequiresPrestaShopModule108OrNewerUpdate" />
            </Typography>
            <Box className={styles.actions}>
              <Button variant="outlined" onClick={handleGoHome} data-testid="wizard-update-plugin-go-home">
                <IntlMessages id="ui.integrationWizard.step5Summary.goToHome" />
              </Button>
            </Box>
          </>
        )}

        {!isCheckingState && !hasError && !isDegraded && storeReady && !previewBuilt
          && artifactMutationSupported && (
          <>
            <CircularProgress size={60} thickness={4} style={{ marginBottom: 24, color: 'var(--kea-color-primary)' }} />
            <Typography variant="h5" className={styles.title}>
              <IntlMessages id="ui.integrationWizard.step5Summary.buildingYourSearchPreview" />
            </Typography>
            <Typography variant="body1" className={styles.description}>
              <IntlMessages id="ui.integrationWizard.step5Summary.weAreGeneratingPreviewAssetsForYourStoreThroughThe" />
            </Typography>

            <Box width="100%" maxWidth={360} mt={1}>
              <Typography variant="body2" color="textSecondary" data-testid="wizard-indexing-status">
                <IntlMessages id="ui.integrationWizard.step5Summary.catalogIsReady" />
              </Typography>
            </Box>

            <Box width="100%" maxWidth={360} mt={2}>
              <Typography variant="body2" color="textSecondary" data-testid="wizard-preview-build-status">
                {isBuildingPreview
                  ? t('ui.integrationWizard.step5Summary.previewBuildRunning')
                  : t('ui.integrationWizard.step5Summary.previewBuildStartingSoon')}
              </Typography>
            </Box>
          </>
        )}
        {isDegraded && !isReady && !hasError && (
          <>
            <CircularProgress size={60} thickness={4} style={{ marginBottom: 24, color: 'var(--kea-color-primary)' }} />
            <Typography variant="h5" className={styles.title}>
              {indexingCopy.title}
            </Typography>
            <Typography variant="body1" className={styles.description}>
              <IntlMessages id="ui.integrationWizard.step5Summary.yourPluginVersionDoesNotSupportDetailedOnboardingStatusWe" />
            </Typography>
          </>
        )}

        {hasError && (
          <>
            <ErrorIcon className={styles.icon} color="error" />
            <Typography variant="h5" color="error" className={styles.title}>
              <IntlMessages id="ui.integrationWizard.step5Summary.previewSetupFailed" />
            </Typography>
            <Typography variant="body1" className={styles.description}>
              {errorMessage}
            </Typography>
            <Box className={styles.actions}>
              <Button variant="outlined" onClick={handleRetry}>
                <IntlMessages id="ui.integrationWizard.step5Summary.tryAgain" />
              </Button>
            </Box>
            <Box className={styles.supportInfo}>
              <Typography variant="body2" color="textSecondary">
                <IntlMessages id="ui.integrationWizard.step5Summary.stillHavingTrouble" />
                {' '}
                <Link
                  component="button"
                  onClick={handleContactSupport}
                  style={{ cursor: 'pointer' }}
                >
                  <IntlMessages id="ui.integrationWizard.step5Summary.contactSupport" />
                </Link>
              </Typography>
            </Box>
          </>
        )}

        {!isCheckingState && !hasError && isReady && (
          <>
            <CheckIcon className={styles.icon} />

            <Typography variant="h4" className={styles.title}>
              <IntlMessages id="ui.integrationWizard.step5Summary.yourSearchPreviewIsReady" />
            </Typography>

            <Typography variant="body1" className={styles.description}>
              <IntlMessages id="ui.integrationWizard.step5Summary.youCanNowPreviewSearchOnYourStoreOnlyYou" />
            </Typography>

            <Box className={styles.actions}>
              <Button
                variant="outlined"
                color="primary"
                size="large"
                startIcon={<LaunchIcon />}
                onClick={handleOpenPreview}
                data-testid="wizard-open-preview"
              >
                <IntlMessages id="ui.integrationWizard.step5Summary.openPreview" />
              </Button>
            </Box>

            <Box mt={4} className={styles.handoffSection}>
              <Typography variant="body1" className={styles.handoffText}>
                <IntlMessages id="ui.integrationWizard.step5Summary.headToHomeToCheckYourStoreSStatusAndNext" />
              </Typography>

              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<HomeIcon />}
                onClick={handleGoHome}
                disabled={isCompleting}
                data-testid="wizard-go-home"
              >
                <IntlMessages id="ui.integrationWizard.step5Summary.goToHome" />
              </Button>
            </Box>
          </>
        )}
      </Box>
    </WizardStepLayout>
  );
};

SummaryStep.propTypes = {
  shopId: PropTypes.string.isRequired,
  onReady: PropTypes.func,
  client: PropTypes.shape({
    persistDraftToGateway: PropTypes.func,
  }),
};

SummaryStep.defaultProps = {
  onReady: null,
  client: null,
};

export default SummaryStep;
